import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Grid, TextField, FormControlLabel, Checkbox, Typography, Box, useTheme, alpha
} from '@mui/material';
import { supabase } from '../../../../supabaseClient';

const PHMeasurementStep = forwardRef(({ currentStep, formulation }, ref) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    target_ph: '',
    max_acceptable_ph: '',
    min_acceptable_ph: '',
    ph_meter_brand: '',
    equipment_id: '',
    electrode_style: '',
    calibration_schedule: '',
    buffer_4_brand: '',
    buffer_7_brand: '',
    buffer_10_brand: '',
    buffer_shelf_life: '',
    buffer_storage_temp: '',
    measurement_temperature: '',
    auto_temp_compensation: false,
    stabilization_time: '',
    number_of_readings: '',
    sample_volume: '',
    acid_for_adjustment: '',
    base_for_adjustment: '',
    adjustment_increment: '',
    adjustment_wait_time: '',
    out_of_spec_procedure: '',
    notification_contacts: '',
    trend_monitoring: false,
    electrode_maintenance: false
  });

  // Load existing pH details when component mounts
  useEffect(() => {
    if (currentStep?.ph_details && Object.keys(currentStep.ph_details).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...currentStep.ph_details
      }));
    }
  }, [currentStep?.id]);

  // Expose save function to parent via ref
  useImperativeHandle(ref, () => ({
    saveData: async () => {
      if (!currentStep?.id) return false;

      try {
        const { error } = await supabase
          .from('procedure_steps')
          .update({
            ph_details: formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentStep.id);

        if (error) throw error;
        return true;
      } catch (error) {
        console.error('Error saving pH data:', error);
        return false;
      }
    },
    formData: formData
  }), [formData, currentStep?.id]);

  // All field handlers - ONLY update local state, no database saves
  const handleTargetPhChange = (e) => {
    setFormData(prev => ({...prev, target_ph: e.target.value}));
  };

  const handleMaxAcceptablePhChange = (e) => {
    setFormData(prev => ({...prev, max_acceptable_ph: e.target.value}));
  };

  const handleMinAcceptablePhChange = (e) => {
    setFormData(prev => ({...prev, min_acceptable_ph: e.target.value}));
  };

  const handlePhMeterBrandChange = (e) => {
    setFormData(prev => ({...prev, ph_meter_brand: e.target.value}));
  };

  const handleEquipmentIdChange = (e) => {
    setFormData(prev => ({...prev, equipment_id: e.target.value}));
  };

  const handleElectrodeStyleChange = (e) => {
    setFormData(prev => ({...prev, electrode_style: e.target.value}));
  };

  const handleCalibrationScheduleChange = (e) => {
    setFormData(prev => ({...prev, calibration_schedule: e.target.value}));
  };

  const handleBuffer4BrandChange = (e) => {
    setFormData(prev => ({...prev, buffer_4_brand: e.target.value}));
  };

  const handleBuffer7BrandChange = (e) => {
    setFormData(prev => ({...prev, buffer_7_brand: e.target.value}));
  };

  const handleBuffer10BrandChange = (e) => {
    setFormData(prev => ({...prev, buffer_10_brand: e.target.value}));
  };

  const handleBufferShelfLifeChange = (e) => {
    setFormData(prev => ({...prev, buffer_shelf_life: e.target.value}));
  };

  const handleBufferStorageTempChange = (e) => {
    setFormData(prev => ({...prev, buffer_storage_temp: e.target.value}));
  };

  const handleMeasurementTemperatureChange = (e) => {
    setFormData(prev => ({...prev, measurement_temperature: e.target.value}));
  };

  const handleAutoTempCompensationChange = (e) => {
    setFormData(prev => ({...prev, auto_temp_compensation: e.target.checked}));
  };

  const handleStabilizationTimeChange = (e) => {
    setFormData(prev => ({...prev, stabilization_time: e.target.value}));
  };

  const handleNumberOfReadingsChange = (e) => {
    setFormData(prev => ({...prev, number_of_readings: e.target.value}));
  };

  const handleSampleVolumeChange = (e) => {
    setFormData(prev => ({...prev, sample_volume: e.target.value}));
  };

  const handleAcidForAdjustmentChange = (e) => {
    setFormData(prev => ({...prev, acid_for_adjustment: e.target.value}));
  };

  const handleBaseForAdjustmentChange = (e) => {
    setFormData(prev => ({...prev, base_for_adjustment: e.target.value}));
  };

  const handleAdjustmentIncrementChange = (e) => {
    setFormData(prev => ({...prev, adjustment_increment: e.target.value}));
  };

  const handleAdjustmentWaitTimeChange = (e) => {
    setFormData(prev => ({...prev, adjustment_wait_time: e.target.value}));
  };

  const handleOutOfSpecProcedureChange = (e) => {
    setFormData(prev => ({...prev, out_of_spec_procedure: e.target.value}));
  };

  const handleNotificationContactsChange = (e) => {
    setFormData(prev => ({...prev, notification_contacts: e.target.value}));
  };

  const handleTrendMonitoringChange = (e) => {
    setFormData(prev => ({...prev, trend_monitoring: e.target.checked}));
  };

  const handleElectrodeMaintenanceChange = (e) => {
    setFormData(prev => ({...prev, electrode_maintenance: e.target.checked}));
  };

  return (
    <Box sx={{ 
      p: 4,
      background: `linear-gradient(135deg, ${alpha('#f8f9fa', 0.8)}, ${alpha('#e9ecef', 0.6)})`,
      backdropFilter: 'blur(10px)',
      borderRadius: 4,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
    }}>
      <Box sx={{ 
        mb: 4,
        p: 4,
        borderRadius: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`
        }
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 500, 
          color: theme.palette.primary.main, 
          mb: 1,
          textAlign: 'center'
        }}>
          pH Measurement Protocol
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 500 }}>
          Define comprehensive pH testing procedures and specifications
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Target pH Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.info.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.info.main, 
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              pH Target & Limits
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Target pH"
                  type="number"
                  step="0.1"
                  inputProps={{ min: 0, max: 14 }}
                  fullWidth
                  value={formData.target_ph}
                  onChange={handleTargetPhChange}
                  placeholder="7.0"
                  size="small"
                  helperText="Your ideal pH value"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.info.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.info.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Maximum pH"
                  type="number"
                  step="0.1"
                  inputProps={{ min: 0, max: 14 }}
                  fullWidth
                  value={formData.max_acceptable_ph}
                  onChange={handleMaxAcceptablePhChange}
                  placeholder="7.5"
                  size="small"
                  helperText="Upper limit - reject if higher"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.info.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.info.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Minimum pH"
                  type="number"
                  step="0.1"
                  inputProps={{ min: 0, max: 14 }}
                  fullWidth
                  value={formData.min_acceptable_ph}
                  onChange={handleMinAcceptablePhChange}
                  placeholder="6.5"
                  size="small"
                  helperText="Lower limit - reject if lower"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.info.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.info.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ 
                  p: 3, 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.1)}`
                }}>
                  <Typography variant="body2" sx={{ color: theme.palette.success.dark, fontWeight: 500 }}>
                    pH Range Summary: Target {formData.target_ph || 'X.X'} 
                    (Acceptable: {formData.min_acceptable_ph || 'X.X'} - {formData.max_acceptable_ph || 'X.X'})
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Equipment Setup Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.warning.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.warning.main, 
              mb: 3
            }}>
              Equipment Setup
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="pH Meter Brand"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.ph_meter_brand}
                  onChange={handlePhMeterBrandChange}
                  placeholder="Mettler Toledo, Hanna Instruments, Thermo Orion"
                  size="small"
                  helperText="Brand and model of pH meter"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.warning.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.warning.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Equipment ID/Serial Number"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.equipment_id}
                  onChange={handleEquipmentIdChange}
                  placeholder="pH-001, Serial: ABC123"
                  size="small"
                  helperText="For tracking and calibration records"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.warning.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.warning.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Electrode Type"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.electrode_style}
                  onChange={handleElectrodeStyleChange}
                  placeholder="Glass combination, ISFET, antimony, flat surface"
                  size="small"
                  helperText="Type of pH electrode"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.warning.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.warning.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Calibration Schedule"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.calibration_schedule}
                  onChange={handleCalibrationScheduleChange}
                  placeholder="Before each use, daily, weekly"
                  size="small"
                  helperText="How often to calibrate the meter"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.warning.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.warning.main
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Calibration Procedure Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Calibration Procedure
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="pH 4 Buffer Brand"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.buffer_4_brand}
                  onChange={handleBuffer4BrandChange}
                  placeholder="Hanna, Thermo Fisher, VWR, custom mix"
                  size="small"
                  helperText="Brand/source of pH 4 buffer"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="pH 7 Buffer Brand"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.buffer_7_brand}
                  onChange={handleBuffer7BrandChange}
                  placeholder="Hanna, Thermo Fisher, VWR, custom mix"
                  size="small"
                  helperText="Brand/source of pH 7 buffer"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="pH 10 Buffer Brand"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.buffer_10_brand}
                  onChange={handleBuffer10BrandChange}
                  placeholder="Hanna, Thermo Fisher, VWR, custom mix"
                  size="small"
                  helperText="Brand/source of pH 10 buffer"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Buffer Shelf Life"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.buffer_shelf_life}
                  onChange={handleBufferShelfLifeChange}
                  placeholder="30 days, 6 months, Until expiry date"
                  size="small"
                  helperText="Replace buffers regularly for accuracy"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Buffer Storage Temperature"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.buffer_storage_temp}
                  onChange={handleBufferStorageTempChange}
                  placeholder="Room temperature, 2-8°C, Follow label"
                  size="small"
                  helperText="Check manufacturer instructions"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Measurement Procedure Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Measurement Procedure
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Sample temperature during test"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.measurement_temperature}
                  onChange={handleMeasurementTemperatureChange}
                  placeholder="25°C, Room temperature, Match product temp"
                  size="small"
                  helperText="Temperature affects pH readings"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ pt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={formData.auto_temp_compensation}
                        onChange={handleAutoTempCompensationChange}
                        sx={{
                          color: theme.palette.primary.main,
                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        pH meter automatically adjusts for temperature
                      </Typography>
                    }
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How long to wait for stable reading?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.stabilization_time}
                  onChange={handleStabilizationTimeChange}
                  placeholder="30 seconds, 2 minutes, Until stable"
                  size="small"
                  helperText="Let reading stabilize before recording"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How many readings to take?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.number_of_readings}
                  onChange={handleNumberOfReadingsChange}
                  placeholder="3"
                  size="small"
                  helperText="Multiple readings improve accuracy"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How much sample do you need?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.sample_volume}
                  onChange={handleSampleVolumeChange}
                  placeholder="50ml in beaker, 100ml, small vial"
                  size="small"
                  helperText="Amount of sample needed for testing"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* pH Adjustment Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              pH Adjustment (if needed)
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="What to use to lower pH (make more acidic)?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.acid_for_adjustment}
                  onChange={handleAcidForAdjustmentChange}
                  placeholder="Citric acid, lactic acid, hydrochloric acid"
                  size="small"
                  helperText="Acid solution to decrease pH"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="What to use to raise pH (make more basic)?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.base_for_adjustment}
                  onChange={handleBaseForAdjustmentChange}
                  placeholder="Sodium hydroxide, potassium hydroxide, triethanolamine"
                  size="small"
                  helperText="Base solution to increase pH"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How much to add at a time?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.adjustment_increment}
                  onChange={handleAdjustmentIncrementChange}
                  placeholder="1 drop, 0.1ml, Small amount"
                  size="small"
                  helperText="Add slowly to avoid overshooting"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How long to wait between additions?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.adjustment_wait_time}
                  onChange={handleAdjustmentWaitTimeChange}
                  placeholder="2 minutes, 30 seconds, Until stable"
                  size="small"
                  helperText="Let pH stabilize before adding more"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Quality Control Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.error.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.error.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.error.main, 
              mb: 3
            }}>
              Quality Control & Troubleshooting
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="What to do if pH is outside limits?"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.out_of_spec_procedure}
                  onChange={handleOutOfSpecProcedureChange}
                  placeholder="1. Re-calibrate meter, 2. Test fresh sample, 3. Check electrode condition, 4. If still out of spec, investigate formulation"
                  size="small"
                  helperText="Step-by-step troubleshooting guide"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.error.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.error.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.error.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Who to notify if there's a problem?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.notification_contacts}
                  onChange={handleNotificationContactsChange}
                  placeholder="QC Supervisor, Formulation Chemist, Production Manager"
                  size="small"
                  helperText="People who need to know about pH issues"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': {
                        borderColor: alpha(theme.palette.error.main, 0.3),
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.error.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.error.main
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.trend_monitoring}
                      onChange={handleTrendMonitoringChange}
                      sx={{
                        color: theme.palette.error.main,
                        '&.Mui-checked': {
                          color: theme.palette.error.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Track pH trends over time
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.electrode_maintenance}
                      onChange={handleElectrodeMaintenanceChange}
                      sx={{
                        color: theme.palette.error.main,
                        '&.Mui-checked': {
                          color: theme.palette.error.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Monitor electrode drift and response time
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
});

export default PHMeasurementStep; 